import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  UserPlus,
  UserCheck,
  Star,
  Shield,
  BadgeCheck,
  Newspaper,
  GraduationCap,
  Users,
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface SuggestedUser {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  follower_count: number;
  is_official: boolean;
  badges: {
    type: string;
    verified_at: string;
  }[];
}

interface OnboardingFollowSuggestionsProps {
  topicIds: string[];
  onNext: (followedUserIds: string[]) => void | Promise<void>;
  onSkip: () => void;
}

// Badge type to icon/color mapping
const BADGE_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  official: {
    icon: <Shield size={12} />,
    color: 'bg-iris-500/20 text-iris-400 border-iris-500/30',
    label: 'Official',
  },
  founding_creator: {
    icon: <Star size={12} />,
    color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    label: 'Founding Creator',
  },
  verified_identity: {
    icon: <BadgeCheck size={12} />,
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    label: 'Verified',
  },
  verified_expert: {
    icon: <GraduationCap size={12} />,
    color: 'bg-iris-500/20 text-iris-400 border-iris-500/30',
    label: 'Expert',
  },
  verified_journalist: {
    icon: <Newspaper size={12} />,
    color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    label: 'Journalist',
  },
};

export default function OnboardingFollowSuggestions({
  topicIds,
  onNext,
  onSkip,
}: OnboardingFollowSuggestionsProps) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<SuggestedUser[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const getAuthToken = useCallback(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('authToken');
    return null;
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      const token = getAuthToken();
      try {
        const params = new URLSearchParams();
        topicIds.forEach((id) => params.append('topicIds', id));

        const res = await fetch(
          `${API_URL}/onboarding/suggestions/users?${params.toString()}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );

        if (res.ok) {
          const data = await res.json();
          const fetchedUsers = data.users || data || [];
          setUsers(fetchedUsers);

          // Pre-select official accounts
          const officialIds = new Set<string>();
          fetchedUsers.forEach((user: SuggestedUser) => {
            if (user.is_official) {
              officialIds.add(user.id);
            }
          });
          setSelectedIds(officialIds);
        }
      } catch {
        // Non-critical, show empty state
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [topicIds, getAuthToken]);

  const toggleUser = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    setSaving(true);
    await onNext(Array.from(selectedIds));
    setSaving(false);
  };

  const getPrimaryBadge = (badges: SuggestedUser['badges']) => {
    if (!badges || badges.length === 0) return null;

    // Priority order for displaying badge
    const priority = [
      'official',
      'verified_journalist',
      'verified_expert',
      'verified_identity',
      'founding_creator',
    ];

    for (const type of priority) {
      const badge = badges.find((b) => b.type === type);
      if (badge) return badge;
    }

    return badges[0];
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-blue-500/15 flex items-center justify-center">
          <Users size={28} className="text-blue-400" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">
          {t('onboarding.followTitle')}
        </h3>
        <p className="text-sm text-[#9a9aac]">
          {t('onboarding.followSubtitle')}
        </p>
      </div>

      {/* User Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={28} className="text-blue-500 animate-spin" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12">
          <Users size={40} className="mx-auto text-[#2a2a3a] mb-3" />
          <p className="text-[#9a9aac]">{t('onboarding.noSuggestions')}</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
          {users.map((user) => {
            const isSelected = selectedIds.has(user.id);
            const primaryBadge = getPrimaryBadge(user.badges);
            const badgeConfig = primaryBadge ? BADGE_CONFIG[primaryBadge.type] : null;

            return (
              <div
                key={user.id}
                className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${
                  isSelected
                    ? 'border-blue-500/40 bg-blue-500/10'
                    : 'border-white/6 bg-white/2 hover:bg-white/4'
                }`}
              >
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-linear-to-br/srgb from-iris-500 to-violet-600 flex items-center justify-center text-[#0C0C12] font-semibold text-sm overflow-hidden shrink-0">
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    user.display_name?.charAt(0)?.toUpperCase() || '?'
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-medium text-white truncate">
                      {user.display_name || user.username}
                    </span>
                    {user.is_official && (
                      <span className="inline-flex px-1.5 py-0.5 text-[9px] font-semibold bg-iris-500/15 text-iris-300 rounded-full uppercase tracking-wide">Channel</span>
                    )}
                    {badgeConfig && (
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[12px] font-medium border ${badgeConfig.color}`}
                      >
                        {badgeConfig.icon}
                        {badgeConfig.label}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#9a9aac] mb-1">@{user.username}</p>
                  {user.bio && (
                    <p className="text-sm text-[#8e8e9e] line-clamp-2">{user.bio}</p>
                  )}
                  <p className="text-xs text-[#828294] mt-1">
                    {t('onboarding.followers', { count: user.follower_count || 0 })}
                  </p>
                </div>

                {/* Follow Button */}
                <button
                  onClick={() => toggleUser(user.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all shrink-0 ${
                    isSelected
                      ? 'bg-blue-600 text-white'
                      : 'bg-white/6 text-[#a0a0b0] hover:bg-white/10 hover:text-white border border-white/6'
                  }`}
                >
                  {isSelected ? (
                    <>
                      <UserCheck size={14} />
                      {t('onboarding.following')}
                    </>
                  ) : (
                    <>
                      <UserPlus size={14} />
                      {t('onboarding.follow')}
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 mt-4 border-t border-white/6">
        <button
          onClick={onSkip}
          className="px-4 py-2 text-sm text-[#9a9aac] hover:text-white transition-colors"
        >
          {t('onboarding.skip')}
        </button>

        <div className="flex items-center gap-3">
          {selectedIds.size > 0 && (
            <span className="text-xs text-[#9a9aac]">
              {t('onboarding.selected', { count: selectedIds.size })}
            </span>
          )}
          <button
            onClick={handleContinue}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold bg-iris-600 hover:bg-iris-500 text-[#0C0C12] transition-all disabled:opacity-50"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            {saving ? t('onboarding.saving', 'Saving...') : t('onboarding.continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
